<script lang="ts">
	/**
	 * A `Button` that routes its click through a confirmation dialog before
	 * dispatching `confirm`.
	 *
	 * Use this for anything destructive or irreversible instead of wiring a Modal by
	 * hand — it keeps the "are you sure" copy, the warning glyph and the
	 * cancel/confirm button order identical everywhere, so users learn the shape of
	 * a dangerous action once.
	 *
	 * The trigger button is fully styled by the caller (`color`, `size`, `outline`,
	 * `pill`, `class`) and its label comes from the default slot, so a pending
	 * spinner or icon can be swapped in without this component knowing about it.
	 */

	import { Button, Modal } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import KeyboardAction from './KeyboardAction.svelte';

	/** Verb phrase spliced into the default question, e.g. `delete this user`. */
	export let action: string = 'proceed';

	/**
	 * Full confirmation question, overriding the one built from `action`.
	 *
	 * Left undefined by default so it can track `action` reactively below. A default
	 * assignment would be evaluated only once at init, freezing the message to the
	 * `action` value present when the component first mounted.
	 */
	export let confirmText: string | undefined = undefined;
	$: resolvedConfirmText = confirmText ?? `Are you sure you want to ${action}?`;

	export let disabled: boolean | undefined = false;
	export let pill: boolean | undefined = false;
	export let outline: boolean | undefined = false;
	export let size: 'xs' | 'sm' | 'lg' | 'xl' | 'md' | undefined = 'md';
	export let color: 'red' | 'yellow' | 'green' | 'purple' | 'blue' | 'light' | 'dark' | 'primary' | 'none' | 'alternative' | undefined = undefined;

	let className: string = '';
	export { className as class };

	/**
	 * Set false to fire `confirm` straight from the click.
	 *
	 * Lets a caller keep one button in the markup and decide per-item whether the
	 * action is dangerous enough to warrant the prompt.
	 */
	export let requireConfirmation: boolean = true;

	/**
	 * The two answers. "No, cancel" / "Yes, I'm sure" reads backwards on a dialog that is itself
	 * about cancelling something — "cancel" is then both the thing being asked about and the way
	 * out of being asked — so a caller can name what each button actually does.
	 */
	export let declineLabel: string = 'No, cancel';
	export let acceptLabel: string = "Yes, I'm sure";

	let showModal: boolean = false;
	const dispatcher = createEventDispatcher();
</script>

<!-- Enter accepts the dialog. Mounted only while the dialog is open so the
     listener is torn down with it — a page listing many rows would otherwise
     accumulate one permanent window listener per button. -->
{#if showModal}
	<KeyboardAction
		keyUp={false}
		keyCodes={['Enter', 'NumpadEnter']}
		exclusive
		on:key={() => {
			showModal = false;
			dispatcher('confirm');
		}}
	/>
{/if}

<!--
	A `detail` slot widens the dialog and sits under the question, left-aligned: it is what the
	decision costs (a list of what is lost, a date), which reads as a paragraph and not a caption.
-->
<Modal bind:open={showModal} size={$$slots.detail ? 'md' : 'xs'}>
	<div class="text-center">
		<i class="fa-solid fa-triangle-exclamation fa-3x text-yellow-500 mb-4" />
		<h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">{resolvedConfirmText}</h3>
		{#if $$slots.detail}
			<div class="text-left mb-5">
				<slot name="detail" />
			</div>
		{/if}
		<div class="flex justify-between items-center gap-3">
			<Button on:click={() => (showModal = false)} color="alternative">{declineLabel}</Button>
			<Button
				on:click={() => {
					showModal = false;
					dispatcher('confirm');
				}}
				color="red">{acceptLabel}</Button
			>
		</div>
	</div>
</Modal>

<Button
	{disabled}
	class={className}
	{pill}
	{outline}
	{size}
	{color}
	on:click={() => {
		if (!requireConfirmation) {
			dispatcher('confirm');
			return;
		}

		showModal = true;
	}}
>
	<slot />
</Button>
